import {useMemo, useState} from 'react';
import ReactSelect, {components, OptionProps} from 'react-select';
import {useIntl} from 'react-intl';
import {useQuery} from 'react-query';
import axios from 'axios';
import TableSearchBar from '../../../common/components/table/TableSearchBar';
import Loading from '../../../common/components/Loading';
import CisContact from '../../../classes/api/CisContact';
import {Customer} from '../../../../Domains/Auth/Types/customer';
import Role from '../../../classes/api/Role';
import {fetchBuContacts} from '../../../api/customer';
import {fetchAll} from '../../../api/roles';
import {downloadFile} from '../../documents/api';
import {useGetCustomers} from '../../../../Domains/Auth/Hooks/CustomerManager';
import {useGetUsers} from '../../../../Domains/Auth/Hooks/UserManager';
import styles from './BuManagerV2.module.scss';
import {User} from '../../../../Domains/Auth/Types/User';
import UserManagerV2, {getUsersOnBu, userHasRole, userMatchesSearch} from './UserManagerV2';

const SEARCH_THRESHOLD = 3;

interface FilterOption {
  value: number;
  label: string;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const CheckboxOption = (props: OptionProps<FilterOption, true>): JSX.Element => (
  <components.Option {...props}>
    <div className="d-flex align-items-start">
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        className="me-2 mt-1 flex-shrink-0"
      />
      <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
        {props.label}
      </label>
    </div>
  </components.Option>
);

const BuManagerV2 = (): JSX.Element => {
  const intl = useIntl();

  const {isLoading: isCustomerLoading, data: customers} = useGetCustomers();
  const {isLoading: isUsersLoading, data: users} = useGetUsers(customers?.map((bu) => bu.id) ?? []);
  const {isLoading: isContactsLoading, data: contacts} = useQuery<any, Error, CisContact[]>(
    ['cisContacts'],
    () => fetchBuContacts(),
    {enabled: !isUsersLoading}
  );
  const {isLoading: isRolesLoading, data: roles} = useQuery<any, Error, Role[]>(
    ['roles'],
    () => fetchAll(),
    {enabled: !isContactsLoading}
  );

  const [search, setSearch] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState<FilterOption[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<FilterOption[]>([]);
  const [toggled, setToggled] = useState<Record<number, boolean>>({});

  const isAllLoaded =
    !isCustomerLoading && !isUsersLoading && !isContactsLoading && !isRolesLoading;

  const usersByBu = useMemo(() => {
    const byBu = new Map<number, User[]>();
    customers?.forEach((bu) => byBu.set(bu.id, getUsersOnBu(users ?? [], bu.id)));
    return byBu;
  }, [customers, users]);

  const searchTerm = search.trim().toLowerCase();

  const buNameMatches = (bu: Customer): boolean =>
    bu.name.toLowerCase().includes(searchTerm) || String(bu.id).includes(searchTerm);

  const accountOptions = useMemo<FilterOption[]>(
    () => (customers ?? []).map((bu) => ({value: bu.id, label: `${bu.name} #${bu.id}`})),
    [customers]
  );

  const roleOptions = useMemo<FilterOption[]>(
    () => (roles ?? []).map((role) => ({value: role.id, label: role.name})),
    [roles]
  );

  const accountIds = useMemo(() => selectedAccounts.map((o) => o.value), [selectedAccounts]);
  const roleIds = useMemo(() => selectedRoles.map((o) => o.value), [selectedRoles]);

  const userSearchFor = (bu: Customer): string =>
    searchTerm && !buNameMatches(bu) ? searchTerm : '';

  const filteredCustomers = (customers ?? []).filter((bu) => {
    if (accountIds.length > 0 && !accountIds.includes(bu.id)) {
      return false;
    }
    if (!searchTerm && roleIds.length === 0) {
      return true;
    }
    if (roleIds.length === 0 && buNameMatches(bu)) {
      return true;
    }
    const userSearch = userSearchFor(bu);
    return (usersByBu.get(bu.id) ?? []).some(
      (user) =>
        (!userSearch || userMatchesSearch(user, userSearch)) && userHasRole(user, bu.id, roleIds)
    );
  });

  const countOnBu = (id: number): number => usersByBu.get(id)?.length ?? 0;

  const isOpen = (id: number): boolean => toggled[id] ?? countOnBu(id) > 0;

  const downloadGuide = (): Promise<void> =>
    downloadFile(axios.defaults.baseURL + '/api/files/customer-guide/download');

  return (
    <>
      {!isAllLoaded && <Loading />}

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-6">
        <div>
          <h1 className="fs-2x fw-bold text-dark mb-1">
            {intl.formatMessage({id: 'USER_MANAGER.TABLE.TITLE'})}
          </h1>
          <div className="text-muted">
            {intl.formatMessage({
              id: 'USER_MANAGER.PAGE.SUBTITLE',
              defaultMessage: 'Gérez les utilisateurs et leurs droits pour chacun de vos comptes.',
            })}
          </div>
        </div>
        <button type="button" className="btn btn-light-primary" onClick={downloadGuide}>
          <i className="fa-solid fa-circle-info me-2"></i>
          {intl.formatMessage({id: 'USER_MANAGER.PAGE.GUIDE', defaultMessage: 'Guide'})}
        </button>
      </div>

      {isAllLoaded && customers && (
        <div className="d-flex flex-wrap align-items-center gap-4 mb-6">
          {customers.length > 1 && (
            <div style={{flex: '1 1 260px', maxWidth: 400}}>
              <ReactSelect<FilterOption, true>
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{Option: CheckboxOption}}
                options={accountOptions}
                value={selectedAccounts}
                onChange={(vals) => setSelectedAccounts(vals ? [...vals] : [])}
                placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_ACCOUNTS'})}
                noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
                classNamePrefix="filter-select"
                menuPortalTarget={document.body}
                styles={filterSelectStyles}
              />
            </div>
          )}
          <div style={{flex: '1 1 220px', maxWidth: 350}}>
            <ReactSelect<FilterOption, true>
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{Option: CheckboxOption}}
              options={roleOptions}
              value={selectedRoles}
              onChange={(vals) => setSelectedRoles(vals ? [...vals] : [])}
              placeholder={intl.formatMessage({
                id: 'USER_MANAGER.PAGE.FILTER.ALL_ROLES',
                defaultMessage: 'Tous les rôles',
              })}
              noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
              classNamePrefix="filter-select"
              menuPortalTarget={document.body}
              styles={filterSelectStyles}
            />
          </div>
          {customers.length > SEARCH_THRESHOLD && (
            <div
              className={`ms-auto ${styles.searchBar}`}
              style={{flex: '0 1 320px', minWidth: 220}}
            >
              <TableSearchBar fullWidth onChange={(e) => setSearch(e.target.value)} />
            </div>
          )}
        </div>
      )}

      {isAllLoaded &&
        customers &&
        filteredCustomers.map((customer) => {
          const open = isOpen(customer.id);
          const count = countOnBu(customer.id);
          return (
            <div className="card card-flush mb-4" key={customer.id}>
              <button
                type="button"
                className="card-header btn btn-flush text-start align-items-center rounded-0 px-6"
                aria-expanded={open}
                onClick={() => setToggled((prev) => ({...prev, [customer.id]: !open}))}
              >
                <div className="card-title m-0 d-flex align-items-center gap-3">
                  <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'} text-gray-500`}></i>
                  <span className="fs-3 fw-bold text-dark">{customer.name}</span>
                  <span className="badge badge-light text-gray-600">#{customer.id}</span>
                </div>
                <span className={`badge ${count > 0 ? 'badge-light-primary' : 'badge-light'}`}>
                  <i className="fa-solid fa-users me-2"></i>
                  {count}
                </span>
              </button>
              {open && (
                <div className="card-body p-0">
                  <UserManagerV2
                    customer={customer}
                    customers={customers}
                    users={users!}
                    cisContacts={contacts!}
                    roles={roles!}
                    userSearch={userSearchFor(customer)}
                    roleIds={roleIds}
                  />
                </div>
              )}
            </div>
          );
        })}

      {isAllLoaded && filteredCustomers.length === 0 && (
        <div className="text-center text-muted py-10">
          {intl.formatMessage({
            id: 'USER_MANAGER.PAGE.NO_BU',
            defaultMessage: 'Aucun compte ne correspond à votre recherche ou à vos filtres.',
          })}
        </div>
      )}
    </>
  );
};

export default BuManagerV2;
